import React, { useState } from 'react';
import Button from '~/components/button';
import { ImageZoomRotate } from '~/components/ImageZoomRotate';
import PictureCard from '~/components/pictureCard';
import PictureModal from '~/components/pictureModal';
import {
  ConsultclientCancelDocument,
  ConsultclientCancelInvoice,
  ConsultclientCancelPhoto,
  ConsultclientCancelResponse,
} from '~/services/consult/user/detail/response';
import * as Masks from '~/utils/masks';
import * as St from './styles';

interface IClientCancelDocumentsTable {
  dataResponse?: ConsultclientCancelResponse;
  onClickInvoice: (invoice: ConsultclientCancelInvoice) => void;
}

const sanitizeUrl = (value: string): string => {
  const raw = String(value || '').trim();

  const markdownLinkMatch = raw.match(/\((https?:\/\/[^)\s]+)\)/i);
  const bracketUrlMatch = raw.match(/\[(https?:\/\/[^\]\s]+)\]/i);
  const directUrlMatch = raw.match(/https?:\/\/[^\s"']+/i);

  const extracted = markdownLinkMatch?.[1] || bracketUrlMatch?.[1] || directUrlMatch?.[0] || '';
  return extracted.replace(/%22$/i, '').replace(/["']+$/g, '');
};

const resolvePhotoSrc = (photo: ConsultclientCancelPhoto): string => {
  const raw = String(photo?.valor || '').trim();

  if (!raw) {
    return '';
  }

  if (photo?.encoded) {
    if (raw.startsWith('data:')) {
      return raw;
    }

    return `data:image/jpeg;base64,${raw.replace(/\s+/g, '')}`;
  }

  return sanitizeUrl(raw);
};

const ClientCancelDocumentsTable: React.FC<IClientCancelDocumentsTable> = ({
  dataResponse,
  onClickInvoice,
}) => {
  const documentos: ConsultclientCancelDocument[] = dataResponse?.documentos || [];
  const faturas: ConsultclientCancelInvoice[] = dataResponse?.faturas || [];
  const fotos: ConsultclientCancelPhoto[] = dataResponse?.fotos || [];
  const [expandedPhotoSrc, setExpandedPhotoSrc] = useState<string | null>(null);

  return (
    <>
      <St.SectionTitle>Documentos</St.SectionTitle>
      {documentos?.length ? (
        <St.Table>
          <thead>
            <tr>
              <St.Th>Descricao</St.Th>
              <St.Th>Acoes</St.Th>
            </tr>
          </thead>
          <tbody>
            {documentos.map((documento, index) => {
              const url = sanitizeUrl(documento?.valor);

              return (
                <tr key={`${documento?.descricao}-${index}`}>
                  <St.Td>{documento?.descricao || '-'}</St.Td>
                  <St.Td>
                    <Button
                      size="small"
                      onClick={() => url && window.open(url, '_blank')}
                      disabled={!url}
                    >
                      Abrir documento
                    </Button>
                  </St.Td>
                </tr>
              );
            })}
          </tbody>
        </St.Table>
      ) : (
        <St.EmptyState>Nenhum documento encontrado.</St.EmptyState>
      )}

      <St.SectionTitle>Faturas</St.SectionTitle>
      {faturas?.length ? (
        <St.Table>
          <thead>
            <tr>
              <St.Th>Data Referencia</St.Th>
              <St.Th>Numero do cartao</St.Th>
              <St.Th>Status</St.Th>
              <St.Th>Acoes</St.Th>
            </tr>
          </thead>
          <tbody>
            {faturas.map((fatura) => (
              <tr key={fatura.id_fatura}>
                <St.Td>{fatura.dataReferencia || '-'}</St.Td>
                <St.Td>{Masks.creditCardMask(fatura.numeroCartao).masked || '-'}</St.Td>
                <St.Td>{fatura.status || '-'}</St.Td>
                <St.Td>
                  <Button size="small" onClick={() => onClickInvoice(fatura)}>
                    Abrir fatura
                  </Button>
                </St.Td>
              </tr>
            ))}
          </tbody>
        </St.Table>
      ) : (
        <St.EmptyState>Nenhuma fatura encontrada.</St.EmptyState>
      )}

      <St.SectionTitle>Fotos</St.SectionTitle>
      {fotos?.length ? (
        <St.PhotosGrid>
          {fotos.map((foto, index) => {
            const photoSrc = resolvePhotoSrc(foto);

            return (
              <St.PhotoItem key={`${foto?.descricao || 'foto'}-${index}`}>
                <St.PhotoDescription>{foto?.descricao || '-'}</St.PhotoDescription>

                <St.PhotoButton
                  type="button"
                  onClick={() => photoSrc && setExpandedPhotoSrc(photoSrc)}
                  disabled={!photoSrc}
                >
                  <PictureCard src={photoSrc} width={180} height={120} expand={false} />
                </St.PhotoButton>

                <St.PhotoHint>Clique na imagem para expandir.</St.PhotoHint>
              </St.PhotoItem>
            );
          })}
        </St.PhotosGrid>
      ) : (
        <St.EmptyState>Nenhuma foto encontrada.</St.EmptyState>
      )}

      {expandedPhotoSrc && (
        <PictureModal handleClose={() => setExpandedPhotoSrc(null)}>
          <div
            style={{
              position: 'relative',
              overflow: 'hidden',
              background: '#fff',
            }}
          >
            <ImageZoomRotate image={expandedPhotoSrc} />
          </div>
        </PictureModal>
      )}
    </>
  );
};

export default ClientCancelDocumentsTable;
